import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Phone, Mail, HelpCircle } from 'lucide-react';
import { Button } from './ui/button';
import Header from './Header';
import Footer from './Footer';
import useJsonLd from '../hooks/useJsonLd';
import usePageSeo from '../hooks/usePageSeo';

const FAQ = () => {
  usePageSeo({
    title: "PRP & Regenerative Medicine FAQ | ATL Regen",
    description: "Find answers to common questions about regenerative medicine, PRP therapy, stem cell treatment, pain management, recovery time, and patient care at ATL Regen.",
    canonical: "https://www.atlregen.com/faq",
    ogTitle: "FAQ | ATL Regen Regenerative Medicine Atlanta",
  });

  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "What is Regenerative Medicine?",
      answer: `Regenerative medicine is an innovative medical field that uses substances from your own body to rejuvenate, regenerate, and repair injured muscles, ligaments, and cartilage.

These treatments are natural, minimally invasive, and effective at healing your pain and getting you back on your feet. Often, these treatments involve a simple extraction of blood or bone marrow that is activated. Platelets in the blood are spun in a centrifuge to activate the healing cells from your own body.

The activated substance is then injected into an injury site to enhance your body's own healing potential, targeting wounded joints, muscles, and tendons.`
    },
    {
      question: "What is platelet rich plasma (PRP) and how does it work?",
      answer: `Platelet-rich plasma (PRP) is blood plasma with a high concentration of platelets. Platelets are blood cells that contain large amounts of healing proteins.

These proteins initiate and accelerate new tissue growth within tendons, ligaments, and inter-vertebral discs to repair damage and relieve symptoms for patients with tendinosis or other causes of chronic pain.

Injecting a patient's own PRP into damaged tissue is an effective, safe, and minimally invasive treatment option. By enhancing the body's own natural healing abilities, patients are able to enjoy a faster and more thorough healing process that relieves symptoms, allowing them to return to their regular activities.`
    },
    {
      question: "Does ARMI specialize in Personal Injury?",
      answer: `ARMI specializes in treating people who have pain due to an auto accident. We understand the common types of pain that come from an automobile accident and have developed treatments that are effective, fast, and geared to restoring the patient's previous functioning.`
    },
    {
      question: "What is ARMI's philosophy?",
      answer: `ARMI's philosophy is to "Get you in, Get you better, and Get you going". We understand that going to the doctor can be a hassle, and it's our job to make sure that your treatment is effective, efficient, and pleasant.`
    },
    {
      question: "What is the research that ARMI is doing?",
      answer: `Currently, we are studying the effects that platelet rich plasma can have on lumbar radiculopathy.`
    },
    {
      question: "What is an epidural steroid injection?",
      answer: `An epidural steroid injection, or ESI, is a minimally invasive treatment used to relieve low back and leg pain (sciatica). ESIs have also been shown to be effective in treating pain in the neck (cervical) and mid-spine (thoracic) as well.

While ESIs do not treat the underlying condition, they are often effective in relieving the chronic pain associated with a variety of spinal disorders. Comprised of cortisone and a local anesthetic or saline solution, ESIs work by reducing inflammation and flushing out particles that cause swelling and pain. ESIs are more effective than oral painkillers and steroids, as the medication is delivered directly to the source of the pain and not dispersed throughout the body.

ESIs are typically injected precisely into the epidural space at the site of the presumed pathology, the area between the dura (a membrane covering the spinal nerves) and the posterior disc space.`
    },
    {
      question: "Who is candidate for an epidural steroid injection?",
      answer: `ESIs are used to relieve pain in the lower back, arms, legs and neck that originates from conditions such as:

• Lumbar spinal stenosis
• Lumbar disc herniation
• Degenerative disc disease
• Facet joint or nerve root cysts
• Disc annular tear

ESIs can be used alone to provide pain relief or as part of a rehabilitation program to help a patient perform his/her exercises with less discomfort. Duration of relief can vary dependent upon the condition being treated. If you have a disc extrusion, usually one or two injections will provide long term pain relief because this condition has a favorable natural history.

Meaning that with time the extruded disc resorbs on its own. The ESI significantly reduces the pain and inflammation and will shorten the period of disability greatly. If you are being treated for spinal stenosis (a bony encroachment of the spinal canal), the duration of relief from one injection will be from 3 to 6 months, at which time the ESI may be repeated again. In some patients who are diligent with the spinal exercises, one injection can last up to one year.`
    },
    {
      question: "How do I schedule an appointment?",
      answer: `To schedule an appointment, please contact our office directly at 404-355-2728. For more information about our services, or any other questions or comments, please visit our Contact Us page.`
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((faq) => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer.trim()
      }
    }))
  };

  useJsonLd(faqSchema);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <main>
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[#1B1B3A] via-[#2D2D4A] to-[#1B1B3A] py-16 sm:py-20">
          <div className="container mx-auto px-4 text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
              <HelpCircle className="h-4 w-4" />
              <span>Have Questions?</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Frequently Asked <span className="text-[#E63946]">Questions</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Find answers to the most common questions about regenerative medicine, PRP therapy, stem cell therapy, pain management, treatment options, recovery time, and patient care at ATL Regen. Our goal is to help you make informed decisions about your health and discover non-surgical solutions for lasting pain relief.
            </p>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 sm:py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div 
                    key={index}
                    className="border border-gray-200 rounded-xl overflow-hidden hover:border-[#E63946]/50 transition-colors"
                  >
                    <button
                      onClick={() => toggleFAQ(index)}
                      className="w-full flex items-center justify-between p-5 sm:p-6 text-left bg-white hover:bg-gray-50 transition-colors"
                      data-testid={`faq-${index}`}
                    >
                      <span className="font-semibold text-gray-900 pr-4 text-sm sm:text-base">
                        {faq.question}
                      </span>
                      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${openIndex === index ? 'bg-[#E63946] text-white' : 'bg-gray-100 text-gray-600'}`}>
                        {openIndex === index ? (
                          <ChevronUp className="h-5 w-5" />
                        ) : (
                          <ChevronDown className="h-5 w-5" />
                        )}
                      </div>
                    </button>
                    {openIndex === index && (
                      <div className="px-5 sm:px-6 pb-5 sm:pb-6 bg-gray-50">
                        <div className="pt-2 text-gray-600 text-sm sm:text-base leading-relaxed whitespace-pre-line">
                          {faq.answer}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                Still Have Questions?
              </h2>
              <p className="text-gray-600 mb-8">
                Our team is here to help. Contact us directly and we'll be happy to answer any questions you may have.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg" 
                  className="bg-[#E63946] hover:bg-[#D32F3D] text-white font-semibold px-8"
                  asChild
                >
                  <a href="tel:+14043552728">
                    <Phone className="mr-2 h-5 w-5" />
                    Call (404) 355-2728
                  </a>
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-[#1B1B3A] text-[#1B1B3A] hover:bg-[#1B1B3A] hover:text-white font-semibold px-8"
                  asChild
                >
                  <a href="mailto:info@atlregen.com">
                    <Mail className="mr-2 h-5 w-5" />
                    Email Us
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default FAQ;
